import { useEffect, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { useLocation } from 'react-router-dom';

import { sellerSortOrders } from '@/enums';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getSortedFollowedSellersIds } from '@/redux/modules/filteredItems';
import {
    getFollowedSellerFilters,
    getFollowedSellerIds,
    onFollowSellerFilterChange,
} from '@/redux/modules/followSeller';
import { fetchSellersByIdsIfNeeded } from '@/redux/modules/seller/seller.actions';
import { parseQueryParams } from '@/utils/queryParams';

import FollowedSellersPage from './FollowedSellersPage';
import { FollowedSellersPageQueryParams } from './types/FollowedSellersTypes';

type Props = {
    tab: string;
};

const FollowedSellersPageContainer = ({ tab }: Props) => {
    const dispatch = useAppDispatch();
    const { search } = useLocation();
    const queryParams = parseQueryParams<FollowedSellersPageQueryParams>(search);
    const filters = useAppSelector(getFollowedSellerFilters);
    const { keyword, page, pageSize, sort } = filters;
    const followedSellerIds = useAppSelector(getFollowedSellerIds);

    const queryFilters = useMemo(
        () => ({
            keyword: queryParams.keyword || '',
            page: Number(queryParams.page) || 1,
            pageSize: Number(queryParams.pageSize) || 24,
            sort: queryParams.sort || sellerSortOrders.NAME_LOWEST_FIRST,
        }),
        [queryParams.keyword, queryParams.page, queryParams.pageSize, queryParams.sort]
    );

    const sellerIds = useAppSelector((state) =>
        getSortedFollowedSellersIds(state, {
            count: pageSize,
            page,
            sort,
            terms: keyword,
        })
    );

    const updateStateFromQuery = () => {
        if (tab === 'followed-auctioneers') {
            if (!isEqual(filters, queryFilters)) {
                dispatch(onFollowSellerFilterChange(filters));
            }
        }
    };

    useEffect(() => {
        dispatch(fetchSellersByIdsIfNeeded(followedSellerIds));
        updateStateFromQuery();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        dispatch(fetchSellersByIdsIfNeeded(followedSellerIds));
    }, [dispatch, followedSellerIds]);

    const onFilterChange = (filters) => {
        dispatch(onFollowSellerFilterChange(filters));
    };

    return (
        <FollowedSellersPage
            onFilterChange={onFilterChange}
            sellerIds={sellerIds}
        />
    );
};

export default FollowedSellersPageContainer;
